import React, { useEffect, useRef, useState } from "react";
import { SendOutlined, UserOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ThemeFitToScreenCard } from "../SurveysStyle";
import { ThemePara1 } from "../../Styles/Theme";
import { ProjectGptChatMassage } from "../../Projects/ProjectsStyle";
import InitialsAvatar from "../../../Components/InitialsAvatar/InitialsAvatar";
import { getUserData } from "../../../utils/permissions";
import TargetAudienceForm from "./TargetAudienceForm";
import {
  MarkdownContent,
  ComposerFooter,
  ComposerInner,
  ComposerShell,
  StyledTextArea,
  ComposerSendButton,
  HeaderToggleButton,
  TargetAudienceFormWrapper,
  ChatPanelStyles,
} from "./ChatPanel.style";

const StreamingMarkdown = ({ content }) => {
  const makeMarkdownSafe = (text) => {
    let safeText = text;

    const codeBlockMatches = safeText.match(/```[\w]*[^`]*$/);
    if (codeBlockMatches) {
      safeText = safeText + '\n```';
    }

    const inlineCodeMatches = safeText.match(/`[^`]*$/);
    if (inlineCodeMatches) {
      safeText = safeText + '`';
    }

    const boldMatches = safeText.match(/\*\*/g);
    if (boldMatches && boldMatches.length % 2 !== 0) {
      safeText = safeText + '**';
    }

    const italicMatches = safeText.match(/(?<!\*)\*(?!\*)/g);
    if (italicMatches && italicMatches.length % 2 !== 0) {
      safeText = safeText + '*';
    }

    if (safeText.endsWith('|')) {
      safeText = safeText + ' ';
    }

    const incompleteLinkMatch = safeText.match(/\[[^\]]*\]\([^)]*$/);
    if (incompleteLinkMatch) {
      safeText = safeText + ')';
    }

    return safeText;
  };

  const safeContent = makeMarkdownSafe(content);

  return (
    <MarkdownContent>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {safeContent}
      </ReactMarkdown>
    </MarkdownContent>
  );
};

const ChatPanel = ({
  surveyTitle,
  messages = [],
  onSendMessage,
  isSending,
  demographics,
  onDemographicsChange,
  isSavingDemographics,
  onSaveDemographics,
  onSaveAndRegenerateQuestions,
  isRegenerating,
  questions = [],
  isLoading = false,
}) => {
  const [chatInput, setChatInput] = useState("");
  const [isTargetAudienceOpen, setIsTargetAudienceOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const formStartRef = useRef(null);
  const userData = getUserData();
  const userName = userData?.name || "User";

  // Get the last message
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isLastMessage = (message) => message?._id === lastMessage?._id;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Scroll to form when it opens
  useEffect(() => {
    if (isTargetAudienceOpen && formStartRef.current) {
      setTimeout(() => {
        formStartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [isTargetAudienceOpen]);

  // Track previous regenerating state to detect completion
  const prevIsRegeneratingRef = useRef(isRegenerating);
  useEffect(() => {
    // Close form when regeneration completes (isRegenerating: true → false)
    if (prevIsRegeneratingRef.current && !isRegenerating && isTargetAudienceOpen) {
      setIsTargetAudienceOpen(false);
    }
    prevIsRegeneratingRef.current = isRegenerating;
  }, [isRegenerating, isTargetAudienceOpen]);

  const handleSend = () => {
    if (!chatInput.trim()) return;
    onSendMessage?.(chatInput.trim());
    setChatInput("");
    setIsTargetAudienceOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToggleTargetAudience = () => {
    setIsTargetAudienceOpen((prev) => !prev);
  };

  return (
    <ChatPanelStyles>
      <ThemeFitToScreenCard
        className="hasHeader chatWrapper"
        title={surveyTitle || "Create a Survey"}
        loading={isLoading}
        extra={
          <HeaderToggleButton
            onClick={handleToggleTargetAudience}
            $isActive={isTargetAudienceOpen}
            aria-label="Toggle Target Audience"
          >
            <UserOutlined />
            <span>{isTargetAudienceOpen ? 'Hide Target Audience' : 'Adjust Target Audience'}</span>
          </HeaderToggleButton>
        }
      >
        <div className="scrollable">
          <ProjectGptChatMassage className="allMessagesLeft">
          {messages.map((message) => (
            <div
              className="messageContainer"
              key={message._id}
              data-is-user={message.role === "user" ? "true" : "false"}
            >
              <div className="avatar">
                {message.role === "user" ? (
                  <InitialsAvatar name={userName} size={24} />
                ) : (
                  <img
                    src="/images/chat-logo.svg"
                    alt="CrowdVox"
                    style={{ width: 24, height: 24 }}
                  />
                )}
              </div>
              <div className="messageContent">
                <div className="messageHeader">
                  <div className="messageHeaderLeft">
                    <span className="senderName">{message.role === "user" ? "You" : "CrowdVox"}</span>
                    {message.stream_state === "streaming" && <ThemePara1 className="shinyText smallFont">Thinking...</ThemePara1>}
                  </div>
                </div>
                <div className="messageText">
                  {message.role === "assistant" ? (
                    message.stream_state === "complete" ? (
                      <MarkdownContent>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.message_text}</ReactMarkdown>
                      </MarkdownContent>
                    ) : (
                      <StreamingMarkdown content={message.message_text} />
                    )
                  ) : (
                    message.message_text
                  )}
                </div>
                {message.attachments?.length > 0 && (
                  <div className="mt-2">
                    {message.attachments.map((file) => (
                      <a key={file.file_key} href={file.file_url} target="_blank" rel="noreferrer" className="attachmentLink">
                        {file.file_name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Target Audience Form - appears below messages when toggled */}
          {isTargetAudienceOpen && (
            <TargetAudienceFormWrapper>
              <div ref={formStartRef} />
              <TargetAudienceForm
                demographics={demographics}
                onChange={onDemographicsChange}
                isSaving={isSavingDemographics || isSending}
                showHeader={true}
                onSave={onSaveDemographics}
                onSaveAndRegenerate={onSaveAndRegenerateQuestions}
                isRegenerating={isRegenerating}
              />
            </TargetAudienceFormWrapper>
          )}

          <div ref={messagesEndRef} />
        </ProjectGptChatMassage>
      </div>
      <ComposerFooter>
        <ComposerInner>
          <ComposerShell>
            <StyledTextArea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Your message"
              onPressEnter={handleKeyPress}
            />
            <ComposerSendButton
              type="button"
              aria-label="Send message"
              onClick={handleSend}
              disabled={isSending || !chatInput.trim()}
            >
              <SendOutlined />
            </ComposerSendButton>
          </ComposerShell>
        </ComposerInner>
      </ComposerFooter>
    </ThemeFitToScreenCard>
    </ChatPanelStyles>
  );
};

export default ChatPanel;
